import { formatNumber, parseNumber } from 'libphonenumber-js';
import numeral from 'numeral';

const numberUtils = {
  fullNumber(rawNum: number) {
    if (rawNum && rawNum >= 0) {
      return numberUtils.prettyPrintNumber(rawNum);
    }

    return '--';
  },
  hasMoreThanTwoDecimalPlaces(value: string | number) {
    const valueAsString = value.toString();
    const decimalIndex = valueAsString.indexOf('.');

    return decimalIndex !== -1 && valueAsString.length - decimalIndex > 3;
  },
  asPercent(value: number) {
    const percent = numeral(value);

    return numeral(percent).divide(100).format('0%');
  },
  convertDollarsToCents(value: number) {
    return numeral(value).multiply(100).value();
  },
  asCurrency(value: number | string, format = '$0,0') {
    const amount = numeral(value);

    return numeral(amount).format(format);
  },
  asCurrencyFromCents(value: number, format = '$0,0') {
    const amount = numeral(value / 100);

    return numeral(amount).format(format);
  },
  asCurrencyWithDefault(value: number, defaultValue = '-', format = '$0,0.00') {
    return value ? this.asCurrency(value, format) : defaultValue;
  },
  removeNonNumeric(value: string) {
    return value.replace(/[^0-9]/g, '');
  },
  prettyPrintNumber(int: number | string) {
    if (typeof int !== 'number' && typeof int !== 'string') {
      console.error(`ERROR: @prettyPrintNumber ${int} should be a number or string`);
      return false;
    }

    const parts = int.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  },
  cleanPhoneNumber(phone: string) {
    // Strip any user formatting from phone numbers because we
    // handle how to display this on the front-end.
    // e.g., "(123) 456-7890" -> "1234567890"
    phone = phone.replace(/[^\d]/g, '');

    if (phone.length > 10) {
      phone = phone.substring(0, 10);
    }

    return phone;
  },
  formatPhoneNumberInput(phone: string) {
    if (typeof phone === 'undefined') {
      return '';
    }

    let phoneNumber = numberUtils.cleanPhoneNumber(phone);
    const phoneLength = phoneNumber.length;

    // Check the length of the phone number and add appropriate masking based on length.
    if (phoneLength === 10) {
      phoneNumber = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, 10)}`;
    } else if (phoneLength > 6) {
      phoneNumber = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(
        6,
        phoneLength,
      )}`;
    } else if (phoneLength > 3) {
      phoneNumber = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, phoneLength)}`;
    } else if (phoneLength > 0) {
      phoneNumber = `(${phoneNumber.substring(0, phoneLength)}`;
    }

    return phoneNumber;
  },
  formatPhoneNumber(phone: string) {
    if (typeof phone === 'undefined') {
      return '';
    }
    // @ts-expect-error - libphonenumber-js types are incomplete
    const parsedPhone = parseNumber(phone, 'US', { extended: true });
    if (Object.keys(parsedPhone).length === 0 || (!parsedPhone.valid && !parsedPhone.possible)) {
      return phone;
    }
    const formatType = parsedPhone.country === 'US' ? 'National' : 'International';
    return formatNumber(parsedPhone, formatType);
  },
  isOnlyIntegers(value: string | number) {
    return !!`${value}`.match(/^[0-9]+$/);
  },
  cleanSSN(value: string) {
    value = value.replace(/[^\d]/g, '');

    if (value.length > 9) {
      value = value.substring(0, 9);
    }

    return value;
  },
  formatSSN(value: string) {
    // Return in the format 123-45-6789.
    let formattedValue = numberUtils.cleanSSN(value);

    // Add dashes
    if (formattedValue.length > 3) {
      formattedValue = `${formattedValue.slice(0, 3)}-${formattedValue.slice(3)}`;
    }
    if (formattedValue.length > 6) {
      formattedValue = `${formattedValue.slice(0, 6)}-${formattedValue.slice(6)}`;
    }

    return formattedValue;
  },
  removeDollarSign(value: string) {
    value = value.replace(/\$/g, '');

    return value;
  },
  formatWithK(value: number, dropLastZero = false) {
    if (Math.abs(value) > 999) {
      const newValue = Math.sign(value) * (Math.abs(value) / 1000);
      let newValueString = newValue.toFixed(1);

      if (dropLastZero && newValueString.substring(newValueString.length - 1) === '0') {
        newValueString = newValueString.replace(/[.]0$/, '');
      }

      return `${newValueString}k`;
    }

    return Math.sign(value) * Math.abs(value);
  },
};

export default numberUtils;
